import { useEffect, useMemo, useState } from 'react';
import {
  normalizeText,
  posEnPais,
  posOrdenadosPorPais,
  POS_PROVIDER_LABELS,
  POS_PROVIDERS,
  type Location,
  type MenuItem,
  type Period,
  type PosIntegrationView,
  type PosProvider,
  type PosProviderAvailability,
  type PosSyncResult,
  type PosUnitsResult,
} from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import {
  useApplyPosUnits,
  useConnectGlopCredentials,
  useConnectLastAppAccount,
  useConnectRestaurantPeCredentials,
  useConnectPosCredentials,
  useConnectLoyverseToken,
  useDisconnectPos,
  useMenuItems,
  usePosIntegrations,
  useStartPosConnection,
  useSyncPosSales,
  useUpdatePosIntegration,
} from '../api/hooks';
import { formatDate, formatPeriod, hoyLocal } from '../format';
import { Badge } from './Badge';
import { Card } from './Card';
import { FieldRow, SelectField, TextField } from './Field';
import { QueryBoundary } from './QueryBoundary';
import { useToast } from './Toast';

/**
 * Integraciones con TPV (Ajustes → Integraciones).
 *
 * Primera tanda (4 sep 2026): Loyverse y Square, los dos con API de
 * autoservicio. Por cada uno, una tarjeta: conectar, decir a que local va
 * cada tienda del TPV, la zona horaria y la hora de corte del dia, y traer un
 * rango de ventas. Las unidades por plato se vuelcan desde Analisis, sobre el
 * periodo que se este mirando (ver `PosUnitsControl`).
 */

const NO_IMPORTAR = '__none__';
const DIA_MS = 86_400_000;
const MAX_DIAS = 92;

const hoy = (): string => hoyLocal();
const sumarDias = (fecha: string, dias: number): string =>
  new Date(new Date(`${fecha}T00:00:00Z`).getTime() + dias * DIA_MS).toISOString().slice(0, 10);
const diasEntre = (a: string, b: string): number =>
  Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / DIA_MS) + 1;

const fechaCorta = (iso: string): string =>
  new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Iconos oficiales servidos desde nuestros propios recursos. Fuentes en public/integrations/sources.json. */
const POS_ICON_EXT: Record<PosProvider, string> = { loyverse: 'svg', square: 'ico', revo: 'png', glop: 'png', lastapp: 'png', bistrosoft: 'svg', polotab: 'png', restaurantpe: 'png', sumup: 'png' };
function PosLogo({ provider }: { provider: PosProvider }): JSX.Element {
  const [failed, setFailed] = useState(false);
  return <span className="pos-logo" aria-hidden="true">{failed ? POS_PROVIDER_LABELS[provider].slice(0, 2) : <img src={`/integrations/${provider}.${POS_ICON_EXT[provider]}`} alt="" width={32} height={32} onError={() => setFailed(true)} />}</span>;
}

function PosTitulo({ provider }: { provider: PosProvider }): JSX.Element {
  return (
    <span className="pos-titulo">
      <PosLogo provider={provider} />
      {POS_PROVIDER_LABELS[provider]}
    </span>
  );
}

/**
 * «Tu punto de venta» (Configuración → Cómo vendes, bloque C, 14 oct 2026):
 * tarjetas con «Conectar», sin buscador ni filtros. El conectado va arriba
 * con su estado («Última venta traída: …»); después, los que funcionan en el
 * país de la cuenta y al final el resto. Con `soloLocal`, solo las cuentas
 * que traen ventas a ese local (o que aún no tienen local).
 */
export function PosIntegrationsCard({ locations, currency, country, soloLocal }: { locations: Location[]; currency: string; country?: string; soloLocal?: string }): JSX.Element {
  const query = usePosIntegrations();
  return <QueryBoundary query={query} loadingLabel="Consultando tus puntos de venta…">{data => {
    // Una cuenta con una tienda en otro local y otra sin asignar también es de
    // este local: la sin asignar puede ser la suya (bugbot 15 oct 2026).
    const delLocal = (item: PosIntegrationView) => !soloLocal || !item.stores.length || item.stores.some(s => !s.locationId || s.locationId === soloLocal);
    const entries = posOrdenadosPorPais(country).map(provider => {
      const connections = data.items.filter(item => item.provider === provider && delLocal(item));
      const availability = data.providers[provider] ?? { oauth: false, token: false };
      const soon = !connections.length && !availability.oauth && !availability.token;
      return { provider, connections, availability, soon, enPais: posEnPais(provider, country) };
    }).sort((a, b) => Number(b.connections.length > 0) - Number(a.connections.length > 0) || Number(b.enPais) - Number(a.enPais) || Number(a.soon) - Number(b.soon));
    const conectadas = entries.filter(e => e.connections.length);
    return <section className="pos-catalog" aria-label="Puntos de venta">
      {isDemoMode && <p className="pos-catalog__demo">Estás en la demo. Mira los puntos de venta; podrás conectarlos desde tu cuenta.</p>}
      {!conectadas.length && <p className="cfg-nota">No tienes ninguno conectado. Mientras, anotas tus ventas a mano o con una foto del corte.</p>}
      <div className="pos-catalog__grid">{entries.map(({ provider, connections, availability, soon }) => {
        const error = connections.some(item => item.status === 'error');
        // Lo que trajo de verdad (último día con ventas), no cuándo se sincronizó:
        // antes decía «trajo ventas hoy» con días sin ventas (revisión del 14 oct 2026).
        const ultimaVenta = connections.map(item => item.lastSaleDate).filter((v): v is string => Boolean(v)).sort().at(-1);
        const ultimaSync = connections.map(item => item.lastSyncAt).filter((v): v is string => Boolean(v)).sort().at(-1);
        return <details className={`pos-provider${connections.length ? ' pos-provider--conectado' : ''}`} name="pos-integrations" key={provider}>
          <summary className="pos-provider__summary">
            <PosTitulo provider={provider} />
            {connections.length > 0 && <Badge tone={error ? 'atencion' : 'marca'}>{error ? 'Revisar conexión' : 'Conectado'}</Badge>}
            {soon && <Badge tone="neutro">Próximamente</Badge>}
            <span className="pos-provider__action">{connections.length ? 'Ver' : soon ? 'Ver' : 'Conectar'}</span>
            {connections.length > 0 && <span className="pos-provider__estado">{ultimaVenta ? `Última venta traída: ${formatDate(ultimaVenta)}` : ultimaSync ? `Revisado el ${fechaCorta(ultimaSync)}` : 'Todavía no trae ventas'}</span>}
          </summary>
          <div className="stack pos-provider__body">{soon ? <p className="muted">La conexión con {POS_PROVIDER_LABELS[provider]} aún no está disponible para tu cuenta. Mientras, puedes traer tus ventas desde Ventas → Importar ventas.</p> : <>{connections.map(integration => <ConnectedCard key={integration.connectionId} integration={integration} locations={locations} currency={currency} varias={connections.length > 1} soloLocal={soloLocal} />)}<ConnectCard provider={provider} availability={availability} otraCuenta={connections.length > 0} localId={soloLocal} locations={locations} /></>}</div>
        </details>;
      })}</div>
    </section>;
  }}</QueryBoundary>;
}

/* ------------------------------------------------------------------ */
/* Sin conectar                                                        */
/* ------------------------------------------------------------------ */

/**
 * Con varios locales, cada uno conecta SU punto de venta (15 oct 2026): la
 * conexión se pide con el local (`localId`) y la cuenta va a ese local.
 */
interface DestinoLocal {
  localId?: string;
  locations?: Location[];
}

/** «Conectado» y, si la cuenta ya traía las ventas de otro local, se dice (no se pisa). */
function avisoConectado(view: PosIntegrationView, base: string, { localId, locations = [] }: DestinoLocal): string {
  if (!localId || view.stores.some((st) => st.locationId === localId)) return base;
  const otros = view.stores.map((st) => locations.find((l) => l.locationId === st.locationId)?.name).filter((n): n is string => Boolean(n));
  return otros.length
    ? `${base} Esa cuenta ya trae las ventas de ${[...new Set(otros)].join(' y ')}. Para este local, conecta su propia cuenta o elige la tienda en la lista.`
    : `${base} Elige en la lista qué tienda es la de este local.`;
}

function ConnectCard(props: {
  provider: PosProvider;
  availability: PosProviderAvailability;
  otraCuenta?: boolean;
} & DestinoLocal): JSX.Element {
  if (props.provider === 'bistrosoft' || props.provider === 'polotab' || props.provider === 'sumup') return <CredentialsConnectCard provider={props.provider} localId={props.localId} locations={props.locations} />;
  if (props.provider === 'restaurantpe') return <RestaurantPeConnectCard localId={props.localId} locations={props.locations} />;
  if (props.provider === 'lastapp') return <LastAppConnectCard availability={props.availability} localId={props.localId} locations={props.locations} />;
  return props.provider === 'glop' ? <GlopConnectCard {...props} /> : <StandardConnectCard {...props} />;
}

function LastAppConnectCard({ availability, ...destino }: { availability: PosProviderAvailability } & DestinoLocal): JSX.Element {
  const connect = useConnectLastAppAccount(destino.localId);
  const toast = useToast();
  return (
    <Card title="Conectar tu cuenta">
      <div className="stack stack--sm">
        <p className="form__section-hint">Conecta la cuenta de Last.app habilitada para tu restaurante para traer ventas y unidades vendidas.</p>
        <button type="button" className="btn" disabled={isDemoMode || !availability.token || connect.isPending}
          onClick={() => connect.mutate(undefined, {
            onSuccess: (view) => toast.success(avisoConectado(view, 'Last.app conectado. Revisa los locales antes de traer ventas.', destino)),
            onError: (error) => toast.fail(error),
          })}>
          {connect.isPending ? 'Comprobando…' : 'Conectar Last.app'}
        </button>
      </div>
    </Card>
  );
}

function GlopConnectCard({ otraCuenta = false, ...destino }: { provider: PosProvider; availability: PosProviderAvailability; otraCuenta?: boolean } & DestinoLocal): JSX.Element {
  const connect = useConnectGlopCredentials(destino.localId);
  const toast = useToast();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [abierta, setAbierta] = useState(false);

  if (otraCuenta && !abierta) {
    return <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbierta(true)}>Conectar otra cuenta de GLOP</button>;
  }

  return (
    <Card
      title={otraCuenta ? 'Otra cuenta de GLOP' : 'Conectar tu cuenta'}
      actions={otraCuenta ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbierta(false)}>Cancelar</button> : undefined}
    >
      <div className="stack stack--sm">
        <p className="form__section-hint">
          Conexión de solo lectura: Alexia traerá tickets y unidades vendidas; nunca modificará datos en GLOP.
          Solicita a soporte de GLOP el identificador y el secreto de API de tu restaurante.
        </p>
        <p className="form__section-hint pos-aviso">
          La activación, el plan y cualquier costo de acceso a la API se contratan y facturan directamente con <strong>GLOP</strong>; no forman parte de Alexia Rest.
        </p>
        <FieldRow>
          <TextField label="Identificador de GLOP" value={clientId} onChange={setClientId} placeholder="client_id" />
          <TextField label="Secreto de GLOP" type="password" value={clientSecret} onChange={setClientSecret} placeholder="client_secret" />
          <button
            type="button"
            className="btn"
            disabled={isDemoMode || clientId.trim().length < 3 || clientSecret.trim().length < 8 || connect.isPending}
            onClick={() => connect.mutate(
              { clientId: clientId.trim(), clientSecret: clientSecret.trim() },
              {
                onSuccess: (view) => {
                  setClientId('');
                  setClientSecret('');
                  toast.success(avisoConectado(view, `GLOP conectado: ${view.merchantName ?? 'cuenta'}. Ya puedes traer ventas.`, destino));
                },
                onError: (error) => toast.fail(error),
              },
            )}
          >
            {connect.isPending ? 'Comprobando…' : 'Conectar GLOP'}
          </button>
        </FieldRow>
      </div>
    </Card>
  );
}

function StandardConnectCard({
  provider,
  availability,
  otraCuenta = false,
  ...destino
}: {
  provider: PosProvider;
  availability: PosProviderAvailability;
  /** Ya hay una conexion de este TPV: la tarjeta se ofrece plegada, como «otra cuenta». */
  otraCuenta?: boolean;
} & DestinoLocal): JSX.Element {
  const start = useStartPosConnection(destino.localId);
  const connectToken = useConnectLoyverseToken(destino.localId);
  const toast = useToast();
  const [token, setToken] = useState('');
  const [abierta, setAbierta] = useState(false);
  const label = POS_PROVIDER_LABELS[provider];

  if (otraCuenta && !abierta) {
    return (
      <div className="row">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={isDemoMode || (!availability.oauth && !availability.token)}
          onClick={() => setAbierta(true)}
        >
          Conectar otra cuenta de {label}
        </button>
        <span className="form__section-hint">
          Para un segundo restaurante que tenga su propia cuenta de {label}. Cada cuenta va a su local.
        </span>
      </div>
    );
  }

  return (
    <Card
      title={otraCuenta ? `Otra cuenta de ${label}` : 'Conectar tu cuenta'}
      actions={
        otraCuenta ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbierta(false)}>
            Cancelar
          </button>
        ) : undefined
      }
    >
      <div className="stack stack--sm">
        <div className="row">
          <button
            type="button"
            className="btn"
            disabled={isDemoMode || !availability.oauth || start.isPending}
            onClick={() =>
              start.mutate(provider, {
                onSuccess: ({ url }) => window.location.assign(url),
                onError: (error) => toast.fail(error),
              })
            }
          >
            {start.isPending ? 'Abriendo…' : `Conectar con ${label}`}
          </button>
          {availability.oauth && (
            <span className="form__section-hint">
              Te llevará a {label} para que autorices la lectura de tus ventas y volverás aquí solo.
            </span>
          )}
          {!availability.oauth && !isDemoMode && (
            <span className="form__section-hint">
              {availability.token
                ? 'La conexión directa no está activada; usa el token de acceso.'
                : `La conexión con ${label} todavía no está activada en este entorno.`}
            </span>
          )}
        </div>

        {availability.token && (
          <div className="form__section">
            <p className="form__section-title">Conectar con un token de acceso</p>
            <ol className="form__section-hint pos-pasos">
              <li>Entra en el <strong>Back Office</strong> de Loyverse desde la computadora.</li>
              <li>Menú de la izquierda: <strong>Integraciones → Tokens de acceso</strong>.</li>
              <li>Haz clic en <strong>+ Añadir token de acceso</strong>. Nombre: «Alexia Rest». Sin fecha de caducidad.</li>
              <li>Haz clic en <strong>Guardar</strong>, copia el token y pégalo aquí abajo.</li>
            </ol>
            <p className="form__section-hint">Solo sirve para leer tus recibos. Puedes borrarlo en Loyverse cuando quieras.</p>
            <FieldRow>
              <TextField label="Token de acceso" value={token} onChange={setToken} placeholder="Pega aquí el token" />
              <button
                type="button"
                className="btn"
                disabled={isDemoMode || token.trim().length < 10 || connectToken.isPending}
                onClick={() =>
                  connectToken.mutate(token.trim(), {
                    onSuccess: (view) => {
                      setToken('');
                      toast.success(avisoConectado(view, `Loyverse conectado: ${view.merchantName ?? 'cuenta'}. Revisa a qué local va cada tienda.`, destino));
                    },
                    onError: (error) => toast.fail(error),
                  })
                }
              >
                {connectToken.isPending ? 'Comprobando…' : 'Conectar'}
              </button>
            </FieldRow>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Conectado                                                           */
/* ------------------------------------------------------------------ */

function ConnectedCard({
  integration,
  locations,
  currency,
  varias,
  soloLocal,
}: {
  integration: PosIntegrationView;
  locations: Location[];
  currency: string;
  /** Hay mas conexiones de este TPV: el nombre de la cuenta va en el titulo para distinguirlas. */
  varias: boolean;
  /** Vista de un local: «Desconectar» suelta solo este local si la cuenta trae también otros. */
  soloLocal?: string;
}): JSX.Element {
  const update = useUpdatePosIntegration();
  const sync = useSyncPosSales();
  // Ventas que la ultima sincronizacion manual no pudo importar: se quedan a
  // la vista hasta la siguiente, para poder buscarlas en el TPV.
  const [saltadas, setSaltadas] = useState<{ total: number; lista: NonNullable<PosSyncResult['skippedSales']> } | null>(null);
  const disconnect = useDisconnectPos();
  const toast = useToast();
  const label = POS_PROVIDER_LABELS[integration.provider];

  const [stores, setStores] = useState<Record<string, string>>(() =>
    Object.fromEntries(integration.stores.map((s) => [s.externalId, s.locationId ?? NO_IMPORTAR])),
  );
  const [timezone, setTimezone] = useState(integration.timezone);
  const [hour, setHour] = useState(String(integration.businessDayStartHour));
  useEffect(() => {
    setStores(Object.fromEntries(integration.stores.map((s) => [s.externalId, s.locationId ?? NO_IMPORTAR])));
    setTimezone(integration.timezone);
    setHour(String(integration.businessDayStartHour));
  }, [integration]);

  const activeLocations = locations.filter((l) => l.active);
  const nombreLocal = soloLocal ? activeLocations.find((l) => l.locationId === soloLocal)?.name : undefined;
  const sirveOtros = Boolean(soloLocal) && integration.stores.some((st) => st.locationId !== null && st.locationId !== soloLocal);
  const locationOptions = [
    { value: NO_IMPORTAR, label: 'No importar' },
    ...activeLocations.map((l) => ({ value: l.locationId, label: l.name })),
  ];
  const hourOptions = Array.from({ length: 24 }, (_, h) => ({
    value: String(h),
    label: `${String(h).padStart(2, '0')}:00`,
  }));

  const cambios =
    timezone !== integration.timezone ||
    hour !== String(integration.businessDayStartHour) ||
    integration.stores.some((s) => (s.locationId ?? NO_IMPORTAR) !== stores[s.externalId]);
  const algunaAsignada = Object.values(stores).some((v) => v !== NO_IMPORTAR);

  // Rango a sincronizar: desde el dia siguiente al ultimo sincronizado, o los
  // ultimos 30 dias la primera vez. Nunca mas de 92 dias de golpe.
  const [start, setStart] = useState(() =>
    integration.lastSyncRange ? sumarDias(integration.lastSyncRange.end, -1) : sumarDias(hoy(), -30),
  );
  const [end, setEnd] = useState(hoy);
  const dias = start && end ? diasEntre(start, end) : 0;
  const rangoValido = dias >= 1 && dias <= MAX_DIAS;

  return (
    <Card
      title={
        <span className="pos-titulo">
          <PosLogo provider={integration.provider} />
          {label}
          {varias && integration.merchantName ? ` · ${integration.merchantName}` : ''}
        </span>
      }
      hint={
        <>
          Conectado{integration.merchantName ? ` a «${integration.merchantName}»` : ''}
          {integration.connectedBy ? ` por ${integration.connectedBy}` : ''} el {fechaCorta(integration.connectedAt)}.
        </>
      }
      actions={
        <Badge tone={integration.status === 'connected' ? 'bien' : 'mal'}>
          {integration.status === 'connected' ? 'Conectado' : 'Requiere atención'}
        </Badge>
      }
    >
      <div className="stack">
        {integration.provider === 'sumup' && <p className="form__section-hint pos-aviso" role="alert">Se importan los pagos registrados en SumUp y se sustituyen los ingresos del local en el rango elegido. Comprueba que también estén registrados el efectivo y otros cobros. Las unidades solo se importan cuando SumUp informa productos.</p>}
        {integration.lastSyncError && (
          <p className="form__section-hint" role="alert">
            Último error: {integration.lastSyncError}
          </p>
        )}
        {!integration.lastSyncError && integration.lastSyncWarning && (
          <p className="form__section-hint pos-aviso" role="status">
            Última sincronización: {integration.lastSyncWarning}
          </p>
        )}
        {integration.merchantCurrency && integration.merchantCurrency !== currency.toUpperCase() && (
          <p className="form__section-hint pos-aviso" role="alert">
            Tu punto de venta trabaja en <strong>{integration.merchantCurrency}</strong> y Alexia en <strong>{currency.toUpperCase()}</strong>.
            Los importes se copian tal cual, sin convertir: pon la misma moneda en los dos (la de Alexia, en
            Configuración → Mi restaurante).
          </p>
        )}

        <div className="form__section">
          <p className="form__section-title">Tiendas del punto de venta</p>
          <p className="form__section-hint">
            Di a qué local de Alexia van las ventas de cada tienda. Las que marques «No importar» se ignoran.
          </p>
          <div className="stack stack--sm">
            {integration.stores.map((store) => (
              <div className="row row--between" key={store.externalId}>
                <SelectField
                  label={store.externalName}
                  value={stores[store.externalId] ?? NO_IMPORTAR}
                  onChange={(value) => setStores((prev) => ({ ...prev, [store.externalId]: value }))}
                  options={locationOptions}
                  hint={store.timezone ? `Zona horaria del punto de venta: ${store.timezone}` : undefined}
                />
              </div>
            ))}
            {integration.stores.length === 0 && (
              <p className="form__section-hint">El punto de venta no ha devuelto ninguna tienda activa.</p>
            )}
          </div>
        </div>

        <div className="form__section">
          <p className="form__section-title">Día de negocio</p>
          <FieldRow>
            {/* Revo da las horas ya en el reloj del restaurante: no hay zona que elegir. */}
            {integration.provider !== 'revo' && (
              <TextField
                label="Zona horaria"
                value={timezone}
                onChange={setTimezone}
                hint="Formato IANA, p. ej. Europe/Madrid o America/Mexico_City. Si el punto de venta declara la suya por tienda, manda esa."
              />
            )}
            <SelectField
              label="El día empieza a las"
              value={hour}
              onChange={setHour}
              options={hourOptions}
              hint="Un ticket de la 01:30 cuenta para el día anterior si el día empieza a las 05:00."
            />
          </FieldRow>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={!cambios || update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    provider: integration.provider,
                    connectionId: integration.connectionId,
                    patch: {
                      stores: integration.stores.map((s) => ({
                        externalId: s.externalId,
                        locationId: stores[s.externalId] === NO_IMPORTAR ? null : stores[s.externalId],
                      })),
                      timezone: timezone.trim(),
                      businessDayStartHour: Number(hour),
                    },
                  },
                  {
                    onSuccess: () => toast.success('Configuración de la conexión guardada.'),
                    onError: (error) => toast.fail(error),
                  },
                )
              }
            >
              {update.isPending ? 'Guardando…' : 'Guardar ajustes'}
            </button>
          </div>
        </div>

        <div className="form__section">
          <p className="form__section-title">Traer ventas</p>
          <p className="form__section-hint">
            Cada noche se traen solas las de ayer y hoy. Desde aquí puedes traer un rango (hasta {MAX_DIAS} días
            de una vez). Se sobrescriben los importes de esos días; lo que hayas anotado a mano de comensales,
            mesas o notas se conserva.
            {integration.lastSyncAt && (
              <>
                {' '}
                Última sincronización: {fechaCorta(integration.lastSyncAt)}
                {integration.lastSyncRange &&
                  ` (${formatPeriod(integration.lastSyncRange.start, integration.lastSyncRange.end)})`}
                .
              </>
            )}
          </p>
          <FieldRow>
            <TextField label="Desde" type="date" value={start} onChange={setStart} />
            <TextField label="Hasta" type="date" value={end} onChange={setEnd} />
            <button
              type="button"
              className="btn"
              disabled={!rangoValido || !algunaAsignada || cambios || sync.isPending}
              onClick={() =>
                sync.mutate(
                  { provider: integration.provider, connectionId: integration.connectionId, period: { start, end } },
                  {
                    onSuccess: (result) => {
                      setSaltadas(result.skippedSalesCount ? { total: result.skippedSalesCount, lista: result.skippedSales ?? [] } : null);
                      toast.success(
                        `${result.receipts} ticket${result.receipts === 1 ? '' : 's'} leído${result.receipts === 1 ? '' : 's'}, ${result.days} día${result.days === 1 ? '' : 's'} de venta actualizado${result.days === 1 ? '' : 's'}.` +
                          (result.skippedStores.length > 0
                            ? ` Sin importar: ${result.skippedStores.join(', ')}.`
                            : '') +
                          (result.skippedSalesCount
                            ? ` ${result.skippedSalesCount} ${result.skippedSalesCount === 1 ? 'venta no se pudo importar' : 'ventas no se pudieron importar'}: las tienes detalladas debajo.`
                            : ''),
                      );
                    },
                    onError: (error) => toast.fail(error),
                  },
                )
              }
            >
              {sync.isPending ? 'Trayendo ventas…' : 'Traer ventas'}
            </button>
          </FieldRow>
          {saltadas && (
            <div className="form__section-hint pos-aviso" role="status">
              <p>
                {saltadas.total} {saltadas.total === 1 ? 'venta no se ha importado' : 'ventas no se importaron'}; el
                resto de esos días sí. Búscalas en {label} y compara el mes en el cuadre de Ventas.
              </p>
              <ul>
                {saltadas.lista.map((v) => (
                  <li key={`${v.externalId}-${v.date}`}>
                    {formatDate(v.date)}
                    {v.document ? ` · ${v.document}` : ` · venta ${v.externalId}`}: {v.reason}
                  </li>
                ))}
                {saltadas.total > saltadas.lista.length && <li>…y {saltadas.total - saltadas.lista.length} más.</li>}
              </ul>
            </div>
          )}
          {!algunaAsignada && (
            <p className="form__section-hint">Asigna al menos una tienda a un local para poder traer ventas.</p>
          )}
          {cambios && algunaAsignada && (
            <p className="form__section-hint">Guarda los ajustes antes de traer ventas.</p>
          )}
          {dias > MAX_DIAS && (
            <p className="form__section-hint">Ese rango tiene {dias} días. Tráelo en tramos de {MAX_DIAS} como mucho.</p>
          )}
        </div>

        <div className="row">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={disconnect.isPending}
            onClick={() => {
              const pregunta = sirveOtros
                ? `¿Dejar de traer las ventas de ${label} a ${nombreLocal ?? 'este local'}? Los demás locales siguen igual.`
                : `¿Desconectar ${label}? Las ventas ya traídas se quedan; solo dejan de actualizarse.`;
              if (!window.confirm(pregunta)) return;
              disconnect.mutate({ provider: integration.provider, connectionId: integration.connectionId, locationId: soloLocal }, {
                onSuccess: () => toast.notify(sirveOtros ? `${label} ya no trae ventas a ${nombreLocal ?? 'este local'}.` : `${label} desconectado.`),
                onError: (error) => toast.fail(error),
              });
            }}
          >
            {sirveOtros ? `Desconectar de ${nombreLocal ?? 'este local'}` : 'Desconectar'}
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Unidades vendidas (Analisis)                                        */
/* ------------------------------------------------------------------ */

/**
 * Boton «Traer unidades del TPV» de la pagina de Analisis, con el
 * emparejamiento a mano de los productos que no coinciden con la carta.
 * Solo se pinta si hay algun TPV conectado.
 */
export function PosUnitsControl({ period }: { period: Period }): JSX.Element | null {
  const integrations = usePosIntegrations();
  const menuItems = useMenuItems();
  const apply = useApplyPosUnits();
  const update = useUpdatePosIntegration();
  const toast = useToast();
  const [result, setResult] = useState<PosUnitsResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const connected = integrations.data?.items ?? [];
  const [provider, setProvider] = useState<PosProvider | null>(null);
  const activo = provider ?? connected[0]?.provider ?? null;
  // Las unidades van por TPV (suman todas sus cuentas); los emparejamientos a
  // mano se guardan en la primera conexion de ese TPV y se leen de todas.
  const proveedores = POS_PROVIDERS.filter((p) => connected.some((i) => i.provider === p));
  const conexionActiva = connected.find((i) => i.provider === activo);

  const items = useMemo(() => (menuItems.data ?? []).filter((m: MenuItem) => m.active), [menuItems.data]);
  const options = useMemo(
    () => [{ value: '', label: 'Elegir plato…' }, ...items.map((m) => ({ value: m.menuItemId, label: m.name }))],
    [items],
  );

  if (connected.length === 0 || !activo) return null;

  const traer = () =>
    apply.mutate(
      { provider: activo, period },
      {
        onSuccess: (r) => {
          setResult(r);
          setMapping({});
          toast.success(
            `${r.matched.length} plato${r.matched.length === 1 ? '' : 's'} con unidades del punto de venta` +
              (r.unmatched.length > 0 ? `; ${r.unmatched.length} productos sin emparejar.` : '.'),
          );
        },
        onError: (error) => toast.fail(error),
      },
    );

  const pendientes = Object.entries(mapping).filter(([, id]) => id !== '');

  return (
    <div className="toolbar">
    <div className="stack stack--sm">
      <div className="row">
        {proveedores.length > 1 && (
          <SelectField
            label="Punto de venta"
            value={activo}
            onChange={(value) => setProvider(value as PosProvider)}
            options={proveedores.map((p) => ({ value: p, label: POS_PROVIDER_LABELS[p] }))}
          />
        )}
        <button type="button" className="btn" disabled={apply.isPending} onClick={traer}>
          {apply.isPending ? 'Sumando tickets…' : `Traer unidades de ${POS_PROVIDER_LABELS[activo]}`}
        </button>
        <span className="form__section-hint">
          Suma los tickets ya sincronizados del periodo ({formatPeriod(period.start, period.end)}) y llena las
          unidades de cada plato. Si falta algún día, tráelo antes desde Configuración → Integraciones.
        </span>
      </div>

      {result && result.unmatched.length > 0 && (
        <div className="form__section">
          <p className="form__section-title">Productos del punto de venta sin plato en la carta</p>
          <p className="form__section-hint">
            Elige a qué plato corresponde cada uno. Se recordará para las próximas veces.
          </p>
          <div className="stack stack--sm">
            {result.unmatched.map((u) => (
              <div className="row row--between" key={u.name}>
                <SelectField
                  label={`${u.name} · ${u.units} ud.`}
                  value={mapping[u.name] ?? ''}
                  onChange={(value) => setMapping((prev) => ({ ...prev, [u.name]: value }))}
                  options={options}
                />
              </div>
            ))}
          </div>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={pendientes.length === 0 || update.isPending || !conexionActiva}
              onClick={() =>
                conexionActiva &&
                update.mutate(
                  {
                    provider: activo,
                    connectionId: conexionActiva.connectionId,
                    patch: {
                      itemMappings: Object.fromEntries(
                        pendientes.map(([name, id]) => [normalizeText(name), id]),
                      ),
                    },
                  },
                  {
                    onSuccess: () => {
                      toast.success('Emparejamientos guardados. Volviendo a sumar…');
                      traer();
                    },
                    onError: (error) => toast.fail(error),
                  },
                )
              }
            >
              {update.isPending ? 'Guardando…' : 'Guardar emparejamientos y volver a traer'}
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function RestaurantPeConnectCard(destino: DestinoLocal): JSX.Element {
  const connect = useConnectRestaurantPeCredentials(destino.localId);
  const toast = useToast();
  const [domainId, setDomainId] = useState('');
  const [token, setToken] = useState('');
  return <Card title="Conectar tu cuenta"><div className="stack stack--sm">
    <p className="form__section-hint">Solicita a Restaurant.pe el ID de dominio y el token con acceso de lectura a ventas. Se importan ventas en soles; revisa que la moneda de tu restaurante sea PEN.</p>
    <FieldRow>
      <TextField label="ID de dominio" value={domainId} onChange={setDomainId} />
      <TextField label="Token de Restaurant.pe" type="password" value={token} onChange={setToken} />
      <button type="button" className="btn" disabled={isDemoMode || !/^\d+$/.test(domainId.trim()) || token.trim().length < 10 || connect.isPending} onClick={() => connect.mutate({ domainId: domainId.trim(), token: token.trim() }, { onSuccess: (view) => { setToken(''); toast.success(avisoConectado(view, 'Restaurant.pe conectado. Revisa los locales antes de importar.', destino)); }, onError: (error) => toast.fail(error) })}>{connect.isPending ? 'Comprobando…' : 'Conectar Restaurant.pe'}</button>
    </FieldRow>
  </div></Card>;
}

const credentialFields = {
  bistrosoft: [{ key: 'clientId', label: 'API key' }, { key: 'clientSecret', label: 'Secret', secret: true }, { key: 'shopCode', label: 'Código de comercio (ShopCode)' }],
  polotab: [{ key: 'apiKey', label: 'API key', secret: true }, { key: 'restaurantId', label: 'ID de restaurante (UUID)' }],
  sumup: [{ key: 'apiKey', label: 'API key de SumUp', secret: true }, { key: 'merchantCode', label: 'Código de comercio' }],
} satisfies Record<string, Array<{ key: string; label: string; secret?: boolean }>>;
function CredentialsConnectCard({ provider, ...destino }: { provider: 'bistrosoft' | 'polotab' | 'sumup' } & DestinoLocal): JSX.Element {
  const connect = useConnectPosCredentials(destino.localId);
  const toast = useToast();
  const [input, setInput] = useState<Record<string, string>>({ country: 'mx' });
  return <Card title="Conectar tu cuenta"><div className="stack stack--sm">
    <p className="form__section-hint">{provider === 'bistrosoft' ? 'Obtén tu API key y secret en BistroWeb → Desarrolladores. Pide la activación del warehouse y el permiso transactions.read. Conecta cada comercio por separado.' : provider === 'polotab' ? 'Solicita a PoloTab una API key con acceso al restaurante y su ID. Alexia solo consulta pedidos, productos y cobros.' : 'Crea una API key en tu cuenta de SumUp con acceso a comercio e historial de transacciones. Se importan los pagos registrados en SumUp; los productos dependen del detalle enviado al cobrar.'}</p>
    {provider === 'sumup' && <p className="form__section-hint pos-aviso">La importación sustituye los ingresos del local en el rango elegido. Usa esta cuenta solo si SumUp contiene todos sus cobros; una cuenta que registra únicamente tarjeta dejaría fuera el efectivo y otros cobros.</p>}
    <FieldRow>
      {provider === 'bistrosoft' && <label className="field">País<select value={input.country} onChange={(e) => setInput({ ...input, country: e.target.value })}><option value="mx">México</option><option value="ar">Argentina</option></select></label>}
      {credentialFields[provider].map((field) => <TextField key={field.key} label={field.label} type={'secret' in field && field.secret ? 'password' : 'text'} value={input[field.key] ?? ''} onChange={(value) => setInput({ ...input, [field.key]: value })} />)}
      <button type="button" className="btn" disabled={isDemoMode || credentialFields[provider].some((f) => !input[f.key]?.trim()) || connect.isPending} onClick={() => connect.mutate({ provider, input: Object.fromEntries(Object.entries(input).map(([k, v]) => [k, v.trim()])) }, { onSuccess: (view) => { setInput({ country: input.country }); toast.success(avisoConectado(view, `${POS_PROVIDER_LABELS[provider]} conectado. Revisa el local antes de importar.`, destino)); }, onError: (error) => toast.fail(error) })}>{connect.isPending ? 'Comprobando…' : `Conectar ${POS_PROVIDER_LABELS[provider]}`}</button>
    </FieldRow>
  </div></Card>;
}
